import { Camera, NotebookPen } from "lucide-react";

import type { Metadata } from "@/lib/head";
import { Link } from "@/lib/nav";

import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import LinkTabs from "@/components/link-tabs";
import MetaSep from "@/components/meta-sep";
import { cn } from "@/lib/utils";
import { EMPTY_MARK, formatDateTime } from "@buildpy/core/lib/format";
import Money from "@/components/money";
import { currentTotalOf } from "@buildpy/core/modules/change-order/calc";
import StaffAsCard from "@/modules/as-request/ui/staff-as-card";
import ChangeOrderList from "@/modules/change-order/ui/change-order-list";
import MessageList from "@/modules/message/ui/message-list";
import { canStartChangeOrder, changeableLogFilter, newChangeOrderPath } from "@buildpy/core/modules/change-order/view";
import StartQuoteButton from "@/modules/customer/ui/start-quote-button";
import PortfolioDraftButton from "@/modules/portfolio/ui/portfolio-draft-button";
import { endsAfterContract } from "@buildpy/core/modules/project/build-reschedule";
import { logDateBounds } from "@buildpy/core/modules/project/build-log";
import {
  buildPhaseChart,
  changeOrderStripOf,
  paymentStripOf,
  progressStripOf,
} from "@buildpy/core/modules/project/phase-chart";
import { countsPhaseDelay } from "@buildpy/core/modules/project/phase-schedule";
import {
  canClose,
  canComplete,
  canReschedule,
  canSetWarrantyEnd,
  canStart,
  RECORDABLE_PROJECT_STATUSES,
} from "@buildpy/core/modules/project/transitions";
import { projectPrimaryActionOf, REPEAT_QUOTE_LABEL, showsRepeatQuote } from "@buildpy/core/modules/project/primary-action";
import { projectDayPath, showsRecentPhotos } from "@buildpy/core/modules/project/photo-view";
import { isCustomerVisibleLog } from "@buildpy/core/modules/project/customer-visible";
import { photoPhasesOn } from "@buildpy/core/modules/project/photo-phase";
import { CALENDAR_WEEKDAYS } from "@buildpy/core/modules/project/site-calendar";
import { currentPhaseTextOf } from "@buildpy/core/modules/project/site-chart";
import { PROJECT_LOG_KIND_LABEL, type ProjectLog, type ProjectPhoto } from "@buildpy/core/modules/project/types";
import ConnectCodeCard from "@/modules/project/ui/connect-code-card";
import CustomerLinkButton from "@/modules/project/ui/customer-link-button";
import DDayBadge from "@/modules/project/ui/d-day-badge";
import LogComposeSheet from "@/modules/project/ui/log-compose-sheet";
import LogList from "@/modules/project/ui/log-list";
import NoticeInquiriesCard from "@/modules/project/ui/notice-inquiries-card";
import PhaseChart from "@/modules/project/ui/phase-chart";
import PhaseTimeline from "@/modules/project/ui/phase-timeline";
import PhotoGrid from "@/modules/project/ui/photo-grid";
import ProgressStrip from "@/modules/project/ui/progress-strip";
import ProjectActions from "@/modules/project/ui/project-actions";
import ProjectStatusBadge from "@/modules/project/ui/status-badge";
import ReviewCard from "@/modules/review/ui/review-card";
import { dDayOf, projectNameOf, scheduleNoticeOf } from "@buildpy/core/modules/project/view";
import type { ProjectView } from "@buildpy/core/views/g3a/project";
import { CrumbName } from "@/shell/crumb";
import PageHeader from "@/shell/page-header";

// 제목은 읽어 둔 현장 이름 — 라우트 head(routes/g3a)가 같은 값을 쓴다.
export const generateMetadata = (data?: ProjectView): Metadata => ({ title: data?.title ?? "현장" });

type DetailTab = "log" | "steps" | "docs";

const tabOf = (tab: string | undefined): DetailTab => (tab === "steps" || tab === "docs" ? tab : "log");

// 「11-02 일요일」.
const dayLabelOf = (date: string): string =>
  `${date.slice(5)} ${CALENDAR_WEEKDAYS[new Date(`${date}T00:00:00Z`).getUTCDay()]}요일`;

// 일지를 날짜별로 묶는다 — 최근 날짜 먼저, 그날 안에서는 쓴 순서.
const logsByDate = (logs: readonly ProjectLog[]): [string, ProjectLog[]][] => {
  const groups = new Map<string, ProjectLog[]>();
  const ordered = [...logs].sort((a, b) => b.log_date.localeCompare(a.log_date) || a.created_at.localeCompare(b.created_at));
  for (const log of ordered) groups.set(log.log_date, [...(groups.get(log.log_date) ?? []), log]);
  return [...groups];
};

// 현장 상세 — 일정·공정표·담당자·기록. 버튼은 전이표(transitions)가 정한다 · 폰에서는 탭(기록·공정표·견적·계약)으로 나눈다.
const Page = ({ data, tab: tabParam }: { data: ProjectView; tab?: string }) => {
  const {
    project,
    phases,
    contractDetail,
    customer,
    members,
    phaseDefs,
    activities,
    changeOrders,
    logs,
    logComments,
    defaultNext,
    warrantyMonths,
    customerLink,
    customerUrl,
    recent,
    messageUnread,
    recentMessages,
    noticeCustomers,
    review,
    today,
    asRequests,
    asRequestOpen,
    connectCode,
  } = data;
  // 지연을 세는 현장(시공중)이면 오늘까지 넓혀 계획을 넘긴 공정이 차트에서 보이게 한다.
  const chartToToday = countsPhaseDelay(project.status);

  const contract = contractDetail?.contract ?? null;
  const contractStatus = contract?.status ?? "VOID";
  const days = project.status === "SCHEDULED" ? dDayOf(project.planned_start_date, today) : null;
  const overContract = endsAfterContract(project.planned_end_date, contract?.end_date ?? null);
  const memberName = new Map(members.map((m) => [m.id, m.name]));
  const phaseNames = new Map(phaseDefs.map((p) => [p.code, p.name]));
  const notice = scheduleNoticeOf(project, phases);
  const canRecord = RECORDABLE_PROJECT_STATUSES.includes(project.status);
  const canEditPhases = project.status === "IN_PROGRESS" && contractStatus === "SIGNED";
  const canChange = contract ? canStartChangeOrder(contract.status, project.status) : false;
  const totals = contract ? currentTotalOf(contract, changeOrders) : null;
  const currentTotal = totals ? totals.current : null;
  const changeCandidates = canChange ? logs.filter(changeableLogFilter(changeOrders)) : [];
  const closed = project.status === "CLOSED";
  const finished = project.status === "WARRANTY" || closed;
  const recentPhotos = recent.photos;
  const chart = buildPhaseChart({
    phases,
    today,
    projectStatus: project.status,
    extendToToday: chartToToday,
    holidays: [...data.holidays, ...(project.schedule_holidays ?? [])],
  });
  const strip = progressStripOf(project, phases, today);
  const paymentStrip = contractDetail ? paymentStripOf(contractDetail.payments) : null;
  const changeOrderStrip = totals ? changeOrderStripOf(changeOrders, totals.accepted_delta) : null;
  const phaseById = new Map(phases.map((p) => [p.id, p]));
  const photoMeta = (photo: ProjectPhoto): string => {
    const phase = photo.project_phase_id ? phaseById.get(photo.project_phase_id) : undefined;
    const phaseName = phase ? (phaseNames.get(phase.phase_code) ?? phase.phase_code) : "";
    const who = photo.uploaded_by ? (memberName.get(photo.uploaded_by) ?? "") : "";
    return [phaseName, who, formatDateTime(photo.created_at)].filter(Boolean).join(" · ");
  };
  const primary = projectPrimaryActionOf({ canStart: canStart(project, contractStatus), canRecord, closed });
  // 제목·주소가 다 비면 「—」 라 헤더에 걸지 않는다.
  const crumbName = projectNameOf(project);
  const tab = tabOf(tabParam);
  // 폰에서만 고른 탭 밖 칸을 숨긴다 — 넓은 화면은 전부 보인다.
  const onTab = (t: DetailTab): string => (t === tab ? "" : "phone:hidden");
  const detailHref = `/app/projects/${project.id}`;
  const tabItems = [
    { tab: "log", label: "기록", href: detailHref },
    { tab: "steps", label: "공정표", href: `${detailHref}?tab=steps` },
    { tab: "docs", label: "견적·계약", href: `${detailHref}?tab=docs` },
  ];
  const startDate = logDateBounds(project, today).min ?? project.planned_start_date;
  const summary = [
    customer ? `${customer.name} 고객` : null,
    startDate ? `착공 ${startDate.slice(5)}` : null,
    project.planned_end_date ? `완료 예정 ${project.planned_end_date.slice(5)}` : null,
  ].filter(Boolean);
  const loggedToday = logs.some((log) => log.log_date === today);
  const todayPhaseText = currentPhaseTextOf(phases, today, phaseNames);
  const photoPhases = photoPhasesOn(phases, today, today);
  const logPhaseOptions = [...phases]
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((p) => ({ id: p.id, name: phaseNames.get(p.phase_code) ?? p.phase_code }));
  const logMeta = (log: ProjectLog): string =>
    [
      isCustomerVisibleLog(log.kind) ? "" : "내부만 보임",
      log.author_id ? (memberName.get(log.author_id) ?? "") : "",
      log.crew_count ? `투입 ${log.crew_count}명` : "",
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <>
      {crumbName !== EMPTY_MARK && <CrumbName name={crumbName} />}
      <PageHeader
        actions={
          // 버튼이 넷까지 늘어 좁은 화면(375px)에서는 줄을 바꾼다.
          <div className="flex flex-wrap items-center justify-start gap-2 md:justify-end">
            {canRecord && (
              <Button
                asChild
                data-testid="project-today-link"
                size="sm"
                variant={primary === "record" ? "primary" : "outline"}>
                <Link href={`/app/projects/${project.id}/today`}>오늘 기록</Link>
              </Button>
            )}
            <CustomerLinkButton reason={customerLink.reason} url={customerUrl} />
            <Button asChild data-testid="project-notice-link" size="sm" variant="outline">
              <Link href={`/app/projects/${project.id}/notice`}>공사 안내문</Link>
            </Button>
            {finished && (
              <PortfolioDraftButton
                portfolioId={data.draftId}
                primary={primary === "portfolio"}
                projectId={project.id}
              />
            )}
            {showsRepeatQuote(project.status) && (
              <StartQuoteButton customerId={project.customer_id} label={REPEAT_QUOTE_LABEL} variant="outline" />
            )}
            <ProjectActions
              assigneeId={project.assignee_id}
              canClose={canClose(project)}
              canComplete={canComplete(project)}
              canReschedule={canReschedule(project, phases, contractStatus)}
              canSetWarrantyEnd={canSetWarrantyEnd(project)}
              canStart={canStart(project, contractStatus)}
              completedOn={project.completion_date}
              completionMin={logDateBounds(project, today).min}
              defaultNext={defaultNext}
              members={members}
              plannedStartDate={project.planned_start_date}
              projectId={project.id}
              readOnly={closed}
              rev={project.rev}
              today={today}
              warrantyEnd={project.warranty_end_date}
              warrantyMonths={warrantyMonths}
            />
          </div>
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-3 md:gap-x-2 md:gap-y-1">
            <ProjectStatusBadge status={project.status} />
            {contract && (
              <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/contracts/${contract.id}`}>
                계약 {contract.contract_no}
              </Link>
            )}
            <MetaSep />
            {customer ? (
              <Link className="tap-target relative underline-offset-2 hover:underline" href={`/app/customers/${customer.id}`}>
                {customer.name}
              </Link>
            ) : (
              <span>{EMPTY_MARK}</span>
            )}
            {project.site_address && (
              <>
                <MetaSep />
                <span>{project.site_address}</span>
              </>
            )}
          </span>
        }
        title={project.title || "현장"}
      />

      <div className="flex flex-col gap-y-2 md:hidden" data-testid="project-phone-tabs">
        {summary.length > 0 && <p className="truncate text-sm text-muted-foreground">{summary.join(" · ")}</p>}
        <LinkTabs activeHref={tabItems.find((t) => t.tab === tab)?.href ?? detailHref} aria-label="현장 상세" items={tabItems} />
      </div>

      {contract?.status === "TERMINATED" && (
        <section
          className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
          data-testid="project-contract-terminated">
          <span className="font-semibold text-destructive">계약 해지됨</span>
          <span className="tabular-nums">{formatDateTime(contract.terminated_at)}</span>
          <span className="text-muted-foreground">{contract.terminate_reason || EMPTY_MARK}</span>
          <span className="text-muted-foreground">착공·착공일 변경을 할 수 없습니다.</span>
        </section>
      )}

      {closed && (
        <section
          className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg border border-border bg-muted/40 p-3 text-sm"
          data-testid="project-closed">
          <span className="font-semibold">완료된 현장</span>
          <span className="tabular-nums">{formatDateTime(project.closed_at)}</span>
          <span className="text-muted-foreground">읽기 전용입니다 — 공정·기록·담당자를 바꿀 수 없습니다.</span>
        </section>
      )}

      {overContract && (
        <section
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          data-testid="project-over-contract">
          준공 예정일({project.planned_end_date})이 계약서의 준공일({contract?.end_date})을 넘깁니다. 고객과 일정을 다시
          맞춰 주세요.
        </section>
      )}

      <div className={onTab("steps")}>
        <ProgressStrip
          changeOrders={changeOrderStrip}
          countsDelay={countsPhaseDelay(project.status)}
          payments={paymentStrip}
          strip={strip}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_340px]">
        <div className="flex min-w-0 flex-col gap-y-3">
          <div className={cn("flex min-w-0 flex-col gap-y-3", onTab("steps"))}>
            {phases.length > 0 && <PhaseChart chart={chart} names={phaseNames} />}
            <h2 className="text-sm font-semibold">공정표</h2>
            <PhaseTimeline
              editableProjectId={canEditPhases ? project.id : null}
              names={phaseNames}
              phases={phases}
              reasonEditableProjectId={canRecord ? project.id : null}
              today={today}
            />
          </div>

          <section className={cn("flex flex-col gap-y-3 md:hidden", onTab("log"))} data-testid="project-log-feed">
            {canRecord && project.status === "IN_PROGRESS" && !loggedToday && (
              <SectionCard className="text-sm" data-testid="project-log-missing" padding="sm">
                <p className="font-semibold">오늘({today.slice(5)}) 기록이 아직 없어요</p>
                {todayPhaseText && <p className="text-muted-foreground">오늘 공정 · {todayPhaseText}</p>}
              </SectionCard>
            )}
            {logs.length === 0 ? (
              <LogList logs={[]} projectId={project.id} />
            ) : (
              logsByDate(logs).map(([date, dayLogs]) => (
                <div key={date} className="flex flex-col gap-y-2">
                  <h2 className="text-sm font-semibold tabular-nums">{dayLabelOf(date)}</h2>
                  <LogList
                    logs={dayLogs.map((log) => ({
                      id: log.id,
                      kind: log.kind,
                      body: log.body,
                      meta: logMeta(log),
                      canDelete: false,
                      weather: log.weather ?? null,
                      comments: logComments.filter((c) => c.log_id === log.id),
                    }))}
                    projectId={project.id}
                  />
                </div>
              ))
            )}
          </section>

          {showsRecentPhotos(project.status, recent.total) && (
            <section className={cn("mt-2 flex flex-col gap-y-2", onTab("log"))} data-testid="project-recent-photos">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">
                  최근 사진 <span className="font-normal tabular-nums text-muted-foreground">{recent.total}</span>
                </h2>
                {recentPhotos[0] && (
                  <Link
                    className="text-xs font-medium text-primary hover:underline"
                    data-testid="project-photos-by-day"
                    href={projectDayPath(project.id, recentPhotos[0].log_date)}>
                    날짜별로 보기
                  </Link>
                )}
              </div>
              <PhotoGrid
                gridClassName="grid-cols-4 sm:grid-cols-6 lg:grid-cols-8"
                photos={recentPhotos.map((photo) => ({
                  id: photo.id,
                  caption: photo.caption,
                  meta: photoMeta(photo),
                  canDelete: false,
                }))}
                projectId={project.id}
                testId="project-recent-photo-grid"
              />
            </section>
          )}

          <section className={cn("mt-2 flex flex-col gap-y-2", onTab("docs"))} data-testid="project-change-orders">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold">
                변경공사
                {currentTotal !== null && (
                  <span className="ml-2 font-normal tabular-nums text-muted-foreground" data-testid="project-current-total">
                    현재 계약금액 <Money value={currentTotal} />
                  </span>
                )}
              </h2>
              {canChange && contract && (
                <Button asChild size="sm" variant="outline">
                  <Link href={newChangeOrderPath(contract.id)}>변경공사 만들기</Link>
                </Button>
              )}
            </div>
            <div className="overflow-x-auto">
              <ChangeOrderList changeOrders={changeOrders} testId="project-change-order-list" />
            </div>
            {changeCandidates.length > 0 && contract && (
              <ul className="flex flex-col gap-y-1.5" data-testid="project-change-candidates">
                {changeCandidates.map((log) => (
                  <SectionCard
                    key={log.id}
                    as="li"
                    className="flex items-center justify-between gap-x-2 rounded-md px-2.5 py-1.5 text-sm"
                    padding="none">
                    <span className="min-w-0 truncate">
                      <span className="mr-2 text-xs font-semibold text-muted-foreground">
                        {PROJECT_LOG_KIND_LABEL[log.kind]} · {log.log_date}
                      </span>
                      {log.body.split("\n")[0]}
                    </span>
                    <Link
                      className="shrink-0 text-xs font-medium text-primary hover:underline"
                      href={newChangeOrderPath(contract.id, log.id)}>
                      변경공사로
                    </Link>
                  </SectionCard>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="flex flex-col gap-y-4">
          <SectionCard className={cn("text-sm", onTab("steps"))} padding="sm" data-testid="project-schedule">
            <h2 className="mb-2 text-sm font-semibold">일정</h2>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">착공 예정일</dt>
              <dd className="flex items-center justify-end gap-x-2 tabular-nums">
                {project.planned_start_date ?? EMPTY_MARK}
                <DDayBadge days={days} warnPast />
              </dd>
              <dt className="text-muted-foreground">준공 예정일</dt>
              <dd className="text-right tabular-nums">{project.planned_end_date ?? EMPTY_MARK}</dd>
              <dt className="text-muted-foreground">계약 준공일</dt>
              <dd className="text-right tabular-nums">{contract?.end_date ?? EMPTY_MARK}</dd>
              <dt className="text-muted-foreground">착공한 때</dt>
              <dd className="text-right tabular-nums">{formatDateTime(project.started_at)}</dd>
              <dt className="text-muted-foreground">준공일</dt>
              <dd className="text-right tabular-nums" data-testid="project-completion-date-view">
                {project.completion_date ?? EMPTY_MARK}
              </dd>
              {project.warranty_end_date && (
                <>
                  <dt className="text-muted-foreground">유지보수 종료일</dt>
                  <dd className="text-right tabular-nums" data-testid="project-warranty-end-view">
                    {project.warranty_end_date}
                  </dd>
                </>
              )}
              <dt className="text-muted-foreground">담당자</dt>
              <dd className="text-right">
                {(project.assignee_id && memberName.get(project.assignee_id)) || "미지정"}
              </dd>
            </dl>
            {notice === "holidays-missing" && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="project-holidays-missing">
                공휴일 미반영 — 공휴일을 못 받아 공휴일에도 작업하는 것으로 펼쳤습니다.
              </p>
            )}
            {notice === "unscheduled" && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="project-unscheduled">
                착공일을 정하면 공정 일정이 펼쳐집니다.
              </p>
            )}
          </SectionCard>

          <SectionCard className={cn("text-sm", onTab("log"))} data-testid="project-message-card" padding="sm">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-sm font-semibold">
                고객 대화
                {messageUnread > 0 && (
                  <span data-testid="project-message-unread">
                    <Pill tone="primary">새 글 {messageUnread}</Pill>
                  </span>
                )}
              </h2>
              <Link
                className="text-xs font-medium text-primary hover:underline"
                data-testid="project-messages-link"
                href={`/app/projects/${project.id}/messages`}>
                대화 열기
              </Link>
            </div>
            <MessageList
              compact
              emptyText="아직 주고받은 글이 없습니다."
              messages={recentMessages}
              testId="project-message-recent"
              viewer="STAFF"
            />
          </SectionCard>

          {(finished || review) && (
            <div className={onTab("log")}>
              <ReviewCard canDelete={data.isOwner} projectId={project.id} review={review} />
            </div>
          )}

          <div className={onTab("log")}>
            <NoticeInquiriesCard customers={noticeCustomers} />
          </div>

          <StaffAsCard className={cn("text-sm", onTab("log"))} open={asRequestOpen} projectId={project.id} requests={asRequests} />

          <ConnectCodeCard className={cn("text-sm", onTab("docs"))} closed={closed} code={connectCode} projectId={project.id} />

          <SectionCard className={cn("text-sm", onTab("log"))} padding="sm">
            <h2 className="mb-2 text-sm font-semibold">기록</h2>
            {activities.length === 0 ? (
              <p className="text-muted-foreground">기록된 이력이 없습니다.</p>
            ) : (
              <ul className="flex flex-col gap-y-3">
                {activities.map((activity) => (
                  <li key={activity.id} className="flex flex-col gap-y-0.5">
                    <span>{activity.summary}</span>
                    <span className="text-xs text-muted-foreground">
                      {formatDateTime(activity.created_at)}
                      {activity.actor_id ? ` · ${memberName.get(activity.actor_id) ?? activity.actor_id}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>

      {canRecord && (
        <div
          className="sticky bottom-0 z-10 -mx-4 flex items-center gap-x-2 border-t border-border bg-card px-4 pb-safe-2 pt-2 md:hidden"
          data-testid="project-phone-bar">
          <Button asChild className="min-w-0 flex-1" size="field" variant="outline">
            <Link href={`${detailHref}/camera`}>
              <Camera />
              사진 찍기
            </Link>
          </Button>
          <div className="min-w-0 flex-1">
            <LogComposeSheet
              defaultPhaseId={photoPhases.defaultId}
              logDate={today}
              phases={logPhaseOptions}
              projectId={project.id}
              projectStatus={project.status}
              trigger={
                <Button className="w-full" data-testid="project-log-button" size="field" variant="primary">
                  <NotebookPen />
                  현장일지 쓰기
                </Button>
              }
            />
          </div>
        </div>
      )}
    </>
  );
};

export default Page;
